import React, { useContext } from "react";
import { ApiContext } from "../../../context/MyContext";
import { isExecutiveEmployee } from "../../../utils/accessControl";
import { BookingsWorkspace } from "./BookingsWorkspace";
import { ManagerBookings } from "./ManagerBookings";
import { holdsManagerTitle, isCoachManager } from "./coachRoster";

/**
 * Call Booking. Which screen a caller gets depends on their own standing, and
 * there are three kinds of it:
 *
 * - An **executive** reaches every coach's calendar, so they get the roster of
 *   every Fitness and Nutritionist coach in the tenant — not their reporting
 *   tree — and may open and edit any of them, **their own included**: that is
 *   what "every calendar" means, and an executive whose own slots were
 *   read-only would be locked out of the one calendar they certainly have
 *   standing over. They always keep My calendar beside it, coach-department
 *   role or not: since `dcf5586` an Executive takes calls as whichever coach a
 *   member's **subscription** names them (`resolve_coach_department` — "the
 *   subscription decides"), so an executive assigned as someone's diet coach
 *   has bookings and shifts of their own with no Nutritionist role at all.
 * - A **Fitness/Nutritionist Manager** gets two tabs: My calendar, and My team
 *   — the coaches assigned to them **directly**, which they open one at a time
 *   to publish availability for. See `ManagerBookings`.
 * - **Everyone else**, coaches included, gets the workspace below: one calendar
 *   over a Bookings / My shifts sub-tab pair. Whether their own shifts are editable is
 *   the backend's own rule — executive standing, or a live `Manager`-titled
 *   role — so a coach gets their calendar read-only and an HR or Ops manager
 *   does not.
 *
 * **The fork is free now, and it did not used to be.** There was a third screen
 * for a manager standing above a coach manager, and no context-only predicate
 * could detect one — the backend put no department or title constraint on such
 * a caller's role, so only the reporting tree could answer it, and every
 * employee waited one `/fetch-employee-hierarchy` round trip behind a skeleton
 * to find out which screen they got. That case is gone: `517da9b` deleted
 * `may_act_for`'s depth-2 branch on the grounds that "the manager of a manager
 * is an executive in this organisation", and executive standing is decided
 * synchronously from `employeeData`. So nothing is probed here, and
 * `useCoachRoster` is called only where a roster is actually rendered.
 *
 * **What `isExecutiveEmployee` is and is not.** It matches a department titled
 * `Executive` — the same predicate that gates `/broadcasts` — and it is *not*
 * the backend's `Employees.is_executive` column, which is what `may_act_for`
 * actually checks. The two can disagree. That is tolerable for the same reason
 * the rest of this page's role logic is: `/modify-coach-shift` authorizes
 * server-side, so this only chooses what to paint. A mismatch shows a table
 * whose writes 403 with the API's own message, rather than granting anything.
 *
 * `focus` is a booking to open on arrival (see `BookingsRoute` and
 * `bookingFocus.js`). It is passed down to whichever screen the caller gets;
 * `onFocusHandled` is called once that screen has opened it or given up.
 */
export default function BookingsPage({ focus = null, onFocusHandled } = {}) {
  const { employeeData, departmentsWithRoles } = useContext(ApiContext);

  const isExecutive = isExecutiveEmployee(employeeData);
  const isManager = isCoachManager(employeeData, departmentsWithRoles);
  // `may_act_for`'s branches, mirrored in its own order — widest first. An
  // executive writes **every** calendar, their own included, and needs no title
  // to do it (`517da9b` restored that branch precisely because three executives'
  // slots had become editable by nobody). Everyone else publishing their own
  // availability needs a live `Manager`-titled role in any department.
  const canEditOwnSlots = isExecutive || holdsManagerTitle(employeeData);

  return (
    // A div, not <main>: App.js already wraps every route in <main>, and a
    // second one would give the page two main landmarks.
    //
    // Full-bleed, unlike Broadcasts' single-column forms which cap at
    // max-w-[1120px]: the workspace is a calendar over a full-width day
    // agenda, with a client-activity drawer over the top of it, and it should
    // use the whole main content area the way Messages/Support do.
    <div className="min-h-full bg-bc-page px-4 py-6 sm:px-10 sm:py-9">
      {isExecutive || isManager ? (
        <ManagerBookings
          rosterScope={isExecutive ? "all" : "team"}
          canEditOwnSlots={canEditOwnSlots}
          focus={focus}
          onFocusHandled={onFocusHandled}
        />
      ) : (
        /*
          Shifts are read-only here unless the caller holds a `Manager` title —
          nobody reaching this branch is an executive, since those go to
          ManagerBookings above. It is the rule /modify-coach-shift enforces on
          an own-calendar write: setting a coach's shifts is their manager's job.
        */
        <BookingsWorkspace
          shiftsReadOnly={!canEditOwnSlots}
          tabLabels={{ shifts: "My shifts" }}
          focus={focus}
          onFocusHandled={onFocusHandled}
        />
      )}
    </div>
  );
}
